import { useState, useCallback, useEffect, useRef, RefObject } from 'react';
import {
  LAYOUT_OVERHEAD,
  MIN_PREVIEW_HEIGHT,
  MIN_ASPECT_RATIO,
  MAX_ASPECT_RATIO,
  DEFAULT_UPLOAD_HEIGHT,
} from '@/consts/create';

type UseAspectRatioOptions = {
  contentRef: RefObject<HTMLDivElement>;
  uploadSectionRef: RefObject<HTMLDivElement>;
  hasUploadedMedia: boolean;
  presetFlowId?: string;
};

const useAspectRatio = ({
  contentRef,
  uploadSectionRef,
  hasUploadedMedia,
  presetFlowId,
}: UseAspectRatioOptions) => {
  const [previewAspectRatio, setPreviewAspectRatio] =
    useState(MIN_ASPECT_RATIO);
  const lastRatioRef = useRef(previewAspectRatio);

  const calculateAspectRatio = useCallback(() => {
    const content = contentRef.current;
    const uploadSection = uploadSectionRef.current;
    if (!content) return;

    const contentHeight = content.clientHeight;
    const contentWidth = content.clientWidth;
    const uploadHeight = uploadSection?.scrollHeight || DEFAULT_UPLOAD_HEIGHT;

    const availableForPreview = contentHeight - LAYOUT_OVERHEAD - uploadHeight;
    const neededRatio =
      contentWidth / Math.max(availableForPreview, MIN_PREVIEW_HEIGHT);

    const ratio = Math.max(
      MIN_ASPECT_RATIO,
      Math.min(MAX_ASPECT_RATIO, neededRatio)
    );

    if (Math.abs(ratio - lastRatioRef.current) > 0.1) {
      lastRatioRef.current = ratio;
      setPreviewAspectRatio(ratio);
    }
  }, [contentRef, uploadSectionRef]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    const debouncedCalculate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateAspectRatio, 50);
    };

    calculateAspectRatio();

    const resizeObserver = new ResizeObserver(debouncedCalculate);
    resizeObserver.observe(content);

    const uploadSection = uploadSectionRef.current;
    if (uploadSection) {
      resizeObserver.observe(uploadSection);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [
    calculateAspectRatio,
    hasUploadedMedia,
    presetFlowId,
    contentRef,
    uploadSectionRef,
  ]);

  return { previewAspectRatio, calculateAspectRatio };
};

export default useAspectRatio;
